import React, { useEffect, useRef, useState } from 'react';
import { is } from '../../services';
import { Button, ButtonList, ButtonType } from '../button';
import { Text } from '../text';
import { ModalMarkup } from './ModalEnum';
import ModalTitle from './ModalTitle';
import ModalFooter from './ModalFooter';
import { hashClass } from '../../helpers';
import clsx from 'clsx';
import { useTrilogyContext } from '../../context';
/**
 * Modal Component
 * @param active {boolean} Activated Modal
 * @param title {string} Title Modal
 * @param content {string} Content text for modal
 * @param triggerContent {string} Trigger custom element
 * @param iconName IconName for icon title
 * @param iconColor IconColor for icon title
 * @param ctaCancelOnClick {Function} function for cancel button (appear when set)
 * @param ctaOnClick {Function} On Click Event CTA
 * @param onClose {Function} Additionnal close custom function
 * @param onOpen {Function} Additionnal open custom function
 * @param closeIcon {boolean} Display close icon for Modal
 * @param children {React.ReactNode}
 * @param ctaContent {string} Content cta
 * @param fullwidth {boolean} Fullwidth Modal
 * @param disableHandlingClickOutside {boolean} Disable the handling on outside click event
 * - -------------------------- WEB PROPERTIES -------------------------------
 * @param className {string} Additionnal CSS Classes
 * @param contentClassNames {string} Additionnal CSS Classes for modal content
 * @param triggerMarkup {ModalMarkup} h1|h2|h3|h4|h5|h6|p|span|div|button|a
 * @param triggerClassNames {string} Additionnal CSS Classes for trigger element
 * @param panel {boolean} Panel Side Modal
 * - -------------------------- NATIVE PROPERTIES -------------------------------
 * @param bottom {boolean} If bottom
 * @param swipable {boolean} Swipable Native Modal
 */
const Modal = ({ children, className, contentClassNames, active, title, content, triggerMarkup, triggerContent, triggerClassNames, ctaContent, ctaOnClick, onClose, onOpen, iconName, ctaCancelOnClick, closeIcon, iconColor, panel, footerClassNames, footer, fullwidth, disableHandlingClickOutside = false, testId, ...others }) => {
    const modal = useRef(null);
    const [display, setDisplay] = useState(active || false);
    const { styled } = useTrilogyContext();
    const handleClickOutside = (e) => {
        var _a;
        if ((_a = modal === null || modal === void 0 ? void 0 : modal.current) === null || _a === void 0 ? void 0 : _a.contains(e.target)) {
            return;
        }
        else {
            handleClose(onClose, e);
        }
    };
    useEffect(() => {
        setDisplay(active || false);
    }, [active]);
    useEffect(() => {
        if (display && !disableHandlingClickOutside) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [display, disableHandlingClickOutside]);
    const classes = React.useMemo(() => hashClass(styled, clsx('modal', display && is('active'), panel && is('panel'), fullwidth && is('fullwidth'), className)), [active, display, panel, className, styled]);
    const contentClasses = React.useMemo(() => hashClass(styled, clsx('modal-content', contentClassNames)), [contentClassNames, styled]);
    const footerClasses = React.useMemo(() => clsx('modal-footer', footerClassNames), [footerClassNames, styled]);
    function handleClose(onCloseFunc, e) {
        setDisplay(false);
        if (onCloseFunc) {
            onCloseFunc(e);
        }
    }
    function handleOpen(onOpenFunc, e) {
        setDisplay(true);
        if (onOpenFunc) {
            onOpenFunc(e);
        }
    }
    /**
     * key in Enum works only in TS or with number enum for JS
     * for string enum (as in this case) we need to use Object.values.includes for JS usage
     * string enum aren't reverse mapped so the first solution doesn't work
     */
    const isCorrectMarkup = (stringMarkup) => {
        if (stringMarkup in ModalMarkup || Object.values(ModalMarkup).includes(stringMarkup))
            return true;
    };
    const TriggerTag = React.useMemo(() => (triggerMarkup && isCorrectMarkup(triggerMarkup) ? triggerMarkup : 'button'), [triggerMarkup]);
    const ModalContentButton = ({ ...props }) => (React.createElement(Button, { markup: 'button', className: is('primary'), ...props }));
    const ModalCancelButton = ({ onCloseFunc }) => (React.createElement(Button, { variant: 'SECONDARY', onClick: (e) => {
            handleClose(onCloseFunc, e);
        }, type: ButtonType.BUTTON }, "Annuler"));
    const CloseButton = ({ onCloseFunc }) => (React.createElement("button", { onClick: (e) => {
            handleClose(onCloseFunc, e);
        }, className: hashClass(styled, clsx('modal-close', is('large'))), "aria-label": 'close', type: ButtonType.BUTTON }));
    return (React.createElement("div", { "data-testid": testId },
        triggerContent && (React.createElement(TriggerTag, { onClick: (e) => {
                handleOpen(onOpen, e);
            }, className: hashClass(styled, clsx(triggerClassNames)) }, triggerContent)),
        React.createElement("div", { className: classes, ...others },
            React.createElement("div", { className: hashClass(styled, clsx('modal-background')) }),
            React.createElement("div", { ref: modal, className: contentClasses },
                closeIcon && React.createElement(CloseButton, { onCloseFunc: onClose }),
                (title || iconName) && (React.createElement(ModalTitle, { iconColor: iconColor, iconName: iconName }, title)),
                content && typeof content === 'string' ? React.createElement(Text, null, content) : content,
                children != null && children,
                (ctaOnClick != null || ctaCancelOnClick != null) && (React.createElement(ButtonList, { centered: true, className: is('flex') },
                    ctaOnClick && React.createElement(ModalContentButton, { onClick: ctaOnClick }, ctaContent),
                    ctaCancelOnClick && React.createElement(ModalCancelButton, { onCloseFunc: ctaCancelOnClick }))),
                footer && React.createElement(ModalFooter, { className: footerClasses }, footer)))));
};
export default Modal;
//# sourceMappingURL=Modal.js.map